import React, { useEffect, useRef, useState, useMemo } from 'react';
import './VocabularyPopup.css';
import { useTranslation } from "react-i18next";

const VocabularyPopup = ({ word, wordData, position, visible, onClose, loading, error }) => {
  const { t } = useTranslation();
  const popupRef = useRef(null);
  const [currentPosition, setCurrentPosition] = useState(position);
  //console.log(word)
  //console.log(wordData)

  // Always animate to new position, even if the same
  useEffect(() => {
    setTimeout(() => setCurrentPosition(position), 10);
  }, [position.x, position.y, word]);

  // Render word details based on type
  const renderWordDetails = (type, details) => {
    if (!type || !details) return null;

    switch (type) {
      case "verb":
        return (
          <div className="popup-word-details">
            <div className="popup-label">VERB FORMS</div>
            <div className="word-forms-compact">
              {t("INF")} - {details.base_verb}, {t("V2")} - {details.past_tense}, {t("V3")} - {details.past_participle}, {t("3 SG")} - {details.third_person_singular}
            </div>
          </div>
        );

      case "noun":
        return (
          <div className="popup-word-details">
            <div className="popup-label">NOUN FORMS</div>
            <div className="word-forms-compact">
              {t("SG")} - {details.singular_form}, {t("PL")} - {details.plural_form}
            </div>
          </div>
        );

      case "adjective":
        return (
          <div className="popup-word-details">
            <div className="popup-label">ADJECTIVE FORMS</div>
            <div className="word-forms-compact">
              {t("BASE")} - {word}, {t("COMP")} - {details.comparative}, {t("SUP")} - {details.superlative}
            </div>
          </div>
        );

      case "adverb":
        return (
          <div className="popup-word-details">
            <div className="popup-label">ADVERB FORMS</div>
            <div className="word-forms-compact">
              {t("BASE")} - {word}, {t("COMP")} - {details.comparative}, {t("SUP")} - {details.superlative}
            </div>
          </div>
        );

      case "pronoun":
        const pronounForms = [];
        if (details.is_reflexive_form) pronounForms.push(t("REFL"));
        if (details.is_object_form) pronounForms.push(t("OBJ"));
        if (details.is_possessive_form) pronounForms.push(t("POSS"));

        return pronounForms.length > 0 ? (
          <div className="popup-word-details">
            <div className="popup-label">PRONOUN FORMS</div>
            <div className="word-forms-compact">
              {pronounForms.join(", ")} - {word}
            </div>
          </div>
        ) : null;

      case "determiner":
        return (
          <div className="popup-word-details">
            <div className="popup-label">DETERMINER FORMS</div>
            <div className="word-forms-compact">
              {t("SG")} - {details.singular}, {t("PL")} - {details.plural}
            </div>
          </div>
        );

      case "number":
        return (
          <div className="popup-word-details">
            <div className="popup-label">NUMBER FORMS</div>
            <div className="word-forms-compact">
              {t("PL")} - {details["plural form"] || details.plural_form}
            </div>
          </div>
        );

      case "abbreviation":
        return (
          <div className="popup-word-details">
            <div className="popup-label">ABBREVIATION</div>
            <div className="word-forms-compact">
              {t("FULL")} - {details["full form"] || details.full_form}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Memoize the popup content to prevent unnecessary re-renders during position changes
  const popupContent = useMemo(() => {
    if (!word) return null;

    // Show loading state
    if (loading) {
      return (
        <>
          <div className="popup-header">
            <h3>{word}</h3>
            <button onClick={onClose}>✕</button>
          </div>
          <div className="popup-loading">
            <div className="loading-spinner"></div>
            <p>⏳ Loading...</p>
          </div>
        </>
      );
    }

    // Show error state
    if (error) {
      return (
        <>
          <div className="popup-header">
            <h3>{word}</h3>
            <button onClick={onClose}>✕</button>
          </div>
          <div className="popup-error">
            <div className="error-icon">❌</div>
            <p>{error}</p>
          </div>
        </>
      );
    }

    // Show content when data is available
    if (!wordData) return null;

    return (
      <>
        <div className="popup-header">
          <h3>{word}</h3>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="popup-pronunciation">
          <span>{wordData.phonetic}</span>
          <button onClick={() => console.log('Play audio:', word)}>🔊</button>
        </div>
        <div className="popup-translation">{wordData.translation}</div>

        {wordData.type && wordData.details && renderWordDetails(wordData.type, wordData.details)}

        {wordData.example && (
          <div className="popup-example">
            <div className="popup-label">{t("Example")}</div>
            <div>{wordData.example}</div>
          </div>
        )}
        {wordData.mistakes && (
          <div className="popup-mistakes">
            <div className="popup-label">{t("Common Mistakes")}</div>
            <div>{wordData.mistakes}</div>
          </div>
        )}
      </>
    );
  }, [word, wordData?.phonetic, wordData?.translation, wordData?.example, wordData?.mistakes, wordData?.type, wordData?.details, onClose, loading, error]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={popupRef}
      className="vocabulary-popup"
      style={{
        position: 'absolute',
        left: `${currentPosition.x}px`,
        top: `${currentPosition.y}px`,
        transition: 'left 0.3s ease-out, top 0.3s ease-out, opacity 0.2s',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        zIndex: visible ? 1000 : -1
      }}
    >
      {word && wordData ? popupContent : <div style={{ padding: 16 }}>Loading...</div>}
    </div>
  );
};

export default VocabularyPopup;